import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import MagneticButton from './MagneticButton'; 

// --- Magnetic Wrapper for Social Links ---
const MagneticWrapper = ({ children, className }) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  
  const springConfig = { stiffness: 150, damping: 15, mass: 0.1 };
  const springX = useSpring(x, springConfig);
  const springY = useSpring(y, springConfig);

  const handleMouseMove = (e) => {
    if (!ref.current || window.innerWidth < 768) return; 
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    x.set((clientX - centerX) * 0.2); 
    y.set((clientY - centerY) * 0.2);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: springX, y: springY }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// --- Parallax Magnetic Nav Item ---
const MagneticNavItem = ({ name, path, isDotVisible, closeMenu }) => {
  const ref = useRef(null);
  
  const xText = useMotionValue(0);
  const yText = useMotionValue(0);
  const springTextX = useSpring(xText, { stiffness: 150, damping: 15, mass: 0.1 });
  const springTextY = useSpring(yText, { stiffness: 150, damping: 15, mass: 0.1 });

  const xDot = useMotionValue(0);
  const yDot = useMotionValue(0);
  const springDotX = useSpring(xDot, { stiffness: 150, damping: 15, mass: 0.1 });
  const springDotY = useSpring(yDot, { stiffness: 150, damping: 15, mass: 0.1 });

  const handleMouseMove = (e) => {
    if (!ref.current || window.innerWidth < 768) return;
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const centerX = left + width / 2;
    const centerY = top + height / 2;
    
    const distanceX = clientX - centerX;
    const distanceY = clientY - centerY;

    xText.set(distanceX * 0.3);
    yText.set(distanceY * 0.3);
    xDot.set(distanceX * 0.1);
    yDot.set(distanceY * 0.1);
  };

  const handleMouseLeave = () => {
    xText.set(0); yText.set(0);
    xDot.set(0); yDot.set(0);
  };

  return (
    <div ref={ref} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} className="relative block w-full">
      <Link to={path} onClick={closeMenu} className="relative flex items-center justify-between md:justify-start">
        {/* The Text */}
        <motion.span style={{ x: springTextX, y: springTextY, display: 'inline-block' }}>
          {name}
        </motion.span>

        {/* The Dot */}
        <motion.span 
          style={{ x: springDotX, y: springDotY }}
          className={`
            md:absolute md:-left-12 
            text-white transition-opacity duration-300 ease-in-out
            ${isDotVisible ? 'opacity-100' : 'opacity-0'}
          `}
        >
          •
        </motion.span>
      </Link>
    </div>
  );
};

const NAV_LINKS = [
  { name: 'About', path: '/' },
  { name: 'Work', path: '/work' },
];

const SOCIAL_LINKS = [
  { name: 'LinkedIn', url: 'https://www.linkedin.com/in/deepayu-ninama-2516451a5' },
  { name: 'Instagram', url: 'https://www.instagram.com/deepayuninama/' },
  { name: 'Facebook', url: 'https://www.facebook.com/deepayu.ninama.777/' },
];

export default function Sidebar({ isMenuOpen, closeMenu, toggleMenu }) {
  const location = useLocation();
  const [hoveredPath, setHoveredPath] = useState(null);
  
  // Track window size for exact clip-path center calculation
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- NEW: Block background scrolling when menu is open ---
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup: Ensure scrolling is re-enabled if component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const isDotVisible = (path) => {
    if (hoveredPath === path) return true; 
    if (hoveredPath === null && location.pathname === path) return true; 
    return false; 
  };

  // 1. EXACT Math for the clip-path center based on button padding/height
  // Mobile center: right 46px, top 70px
  // Desktop center: right 144px, top 88px
  const clipCenter = isMobile ? 'calc(100% - 46px) 70px' : 'calc(100% - 144px) 88px';

  return (
    <>
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-xl bg-zinc-900 shadow-xl flex flex-col justify-between z-50 ${
          isMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
        style={{
          clipPath: isMenuOpen 
            ? `circle(150% at ${clipCenter})` 
            : `circle(0px at ${clipCenter})`,
          transition: 'clip-path 0.8s cubic-bezier(0.76, 0, 0.24, 1)'
        }}
      >
        <div>
          {/* Close Button Container */}
          <div className="flex justify-end p-6">
            <MagneticButton 
              onClick={toggleMenu} 
              // Matches Navbar mobile positioning exactly
              className="fixed top-10 right-4 md:right-24 text-white bg-black hover:bg-indigo-600 p-5 md:p-8 rounded-full shadow-lg z-50 transition-colors duration-300" 
              ariaLabel="Close menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-8 md:w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </MagneticButton>
          </div>
          
          {/* Navigation Section */}
          <div className="px-12 md:pl-24 mt-16 md:mt-24">
            <div className="text-black">
              <p className='uppercase font-semibold text-zinc-400 text-sm'>Navigation</p>
              <hr className="h-px border-0 bg-zinc-700 mt-8 w-full md:w-96" />
            </div>
            
            <ul className="flex flex-col items-start justify-start gap-6 md:gap-12 uppercase text-4xl md:text-5xl font-medium text-white mt-12 md:mt-20">
              {NAV_LINKS.map((link) => (
                <li key={link.path} className="w-full" onMouseEnter={() => setHoveredPath(link.path)} onMouseLeave={() => setHoveredPath(null)}>
                  <MagneticNavItem 
                    name={link.name} 
                    path={link.path} 
                    isDotVisible={isDotVisible(link.path)} 
                    closeMenu={closeMenu} 
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Socials Section */}
        <div className="p-10 pb-12 md:pb-16 text-black px-8 md:pl-24">
          <div className='gap-5 grid'>
            <p className='uppercase font-semibold text-zinc-400 text-xs md:text-sm'>
              socials
            </p>
            <div className='flex gap-10 flex-wrap'>
              {SOCIAL_LINKS.map((social) => (
                <MagneticWrapper key={social.name} className="inline-block">
                  <a 
                    href={social.url} 
                    target="_blank" 
                    rel="noreferrer" 
                    className='font-medium text-white text-sm md:text-base hover:text-zinc-300 transition-colors block'
                  >
                    {social.name}
                  </a>
                </MagneticWrapper>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isMenuOpen && (
        <div onClick={closeMenu} className="fixed inset-0 bg-black/60 z-40 transition-opacity duration-900"></div>
      )}
    </>
  );
}